import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";

const ExpenseList = () => {
  const location = useLocation();
  const [expenseData, setExpenseData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await Http.get(`/accounts/expenses`);
      const resRole = await Http.get(`/staff-type-check`);

      // Helper functions to detect ISO UTC strings and format to desired local display
      const isIsoUtcDateString = (str) =>
        typeof str === "string" &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(str);

      const formatUtcToLocalDisplay = (isoStr) => {
        const d = new Date(isoStr); // parsed as UTC, represented in local time
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
        const day = pad2(d.getDate());
        const monthName = months[d.getMonth()];
        const year = d.getFullYear();
        let hours = d.getHours();
        const minutes = pad2(d.getMinutes());
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        if (hours === 0) hours = 12;
        const hh = pad2(hours);
        return `${day}-${monthName}-${year} ${hh}:${minutes} ${ampm}`;
      };

      // Helper to format numeric amount with thousands separators, preserving decimals exactly
      const formatAmountWithGrouping = (val) => {
        if (val === null || val === undefined) return val;
        const str = String(val);
        if (!/^-?\d+(?:\.\d+)?$/.test(str)) return val; // leave as-is if not a pure number
        const isNeg = str.startsWith("-");
        const raw = isNeg ? str.slice(1) : str;
        const [intPart, decPart] = raw.split(".");
        const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const result =
          decPart !== undefined ? `${grouped}.${decPart}` : grouped;
        return isNeg ? `-${result}` : result;
      };

      // Transform only top-level date fields and 'amount' on each item; preserve all other fields exactly
      const formattedData = Array.isArray(res.data)
        ? res.data.map((item) => {
            const formatted = { ...item };
            Object.keys(item).forEach((key) => {
              const val = item[key];
              if (isIsoUtcDateString(val)) {
                formatted[key] = formatUtcToLocalDisplay(val);
              } else if (key === "amount") {
                formatted[key] = formatAmountWithGrouping(val);
              }
            });
            return formatted;
          })
        : res.data;

      setExpenseData(formattedData);
      setRole(resRole.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const cols = [
    { field: "expense_number", header: "Expense Number" },
    { field: "date", header: "Date" },
    { field: "category.name", header: "Category" },
    { field: "bank.name", header: "Bank" },
    { field: "amount", header: "Amount" },
    { field: "description", header: "Description" },
    { field: "created_by.name", header: "Created By" },
    { field: "updated_by.name", header: "Updated By" },
  ];

  const actionTemplate = (item) => {
    return (
      <td className="text-xs text-center">
        <Link
          to={{
            pathname: "/view-expense/" + item.id,
          }}
          className="btn btn-info btn-sm ms-1"
          title="View Expense"
        >
          <i className="fa fa-list" aria-hidden="true"></i>
        </Link>
        {(role === "admin" || role === "accounts") && (
          <Link
            to={{
              pathname: "/edit-expense/" + item.id,
            }}
            className="btn btn-primary btn-sm ml-1"
            title="Edit"
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
          </Link>
        )}
      </td>
    );
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">All Expenses</h5>
            <div className="am-right">
              {(role === "admin" || role === "accounts") && (
                <Link to="/create-expense" className="btn btn-primary">
                  <i className="fa fa-plus"></i> Add New Expense
                </Link>
              )}
            </div>
          </div>

          <div className="am-pagebody">
            <Alert alert={alert} />

            <div className="card pd-20 pd-sm-40">
              <ReactDataTable
                cols={cols}
                receiveData={expenseData}
                isLoading={loading}
                actionTemplate={actionTemplate}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseList;
